import React, { useEffect, useState } from "react";
import { Power, Trash2, Plus, Check } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ADMIN_TRAIN } from "@/constants/testIds";
import { ALL_ROLES, ROLE_LABELS } from "@/lib/permissions";

const EMPTY = { code: "", label: "", allowed_roles: [], max_uses: null, active: true };

export default function AdminAccessCodes() {
  const [codes, setCodes] = useState([]);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    try { const { data } = await api.get("/admin/training/access-codes"); setCodes(data); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const toggle = async (id) => {
    try { await api.post(`/admin/training/access-codes/${id}/toggle`); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  const del = async (id, isSystem) => {
    if (isSystem) { setErr("System-default code cannot be deleted — deactivate it instead."); return; }
    if (!window.confirm("Delete this code? This is permanent.")) return;
    try { await api.delete(`/admin/training/access-codes/${id}`); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  const create = async () => {
    setCreating(true); setErr("");
    try { await api.post("/admin/training/access-codes", form); setForm(EMPTY); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); }
    finally { setCreating(false); }
  };

  const toggleRole = (r) => setForm((f) => ({
    ...f, allowed_roles: f.allowed_roles.includes(r) ? f.allowed_roles.filter((x) => x !== r) : [...f.allowed_roles, r],
  }));

  return (
    <div className="space-y-6">
      <header className="fade-up">
        <div className="eyebrow">Training admin</div>
        <h1 className="headline-xl">Access codes</h1>
        <p className="text-lifted-dim mt-2 text-sm max-w-2xl">
          Codes restrict who can enter specific Lifted experiences. The system-default
          <code className="text-lifted-green mx-1">LIFTED-WELCOME</code> can be deactivated but never deleted.
          Every redemption — success or failure — is logged.
        </p>
      </header>

      {err && <div className="glass-panel p-3 text-rose-300 text-sm border-rose-500/30">{err}</div>}

      <div className="glass-panel p-5">
        <div className="eyebrow !text-lifted-text-dim mb-3">Create access code</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input data-testid={ADMIN_TRAIN.codeCreateInput} className="input-lifted md:col-span-1" placeholder="CODE (auto if blank)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          <input className="input-lifted md:col-span-2" placeholder="Label / purpose" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <input className="input-lifted" type="number" placeholder="Max uses (blank = ∞)" value={form.max_uses ?? ""} onChange={(e) => setForm({ ...form, max_uses: e.target.value === "" ? null : parseInt(e.target.value, 10) })} />
        </div>
        <div className="mt-3">
          <div className="eyebrow !text-lifted-text-dim mb-2">Allowed roles (none = any)</div>
          <div className="flex flex-wrap gap-1.5">
            {ALL_ROLES.map((r) => (
              <button key={r} onClick={() => toggleRole(r)} className={`chip cursor-pointer ${form.allowed_roles.includes(r) ? "chip--green" : ""}`}>
                {ROLE_LABELS[r]}
              </button>
            ))}
          </div>
        </div>
        <button data-testid={ADMIN_TRAIN.codeCreateBtn} onClick={create} disabled={creating} className="btn-lifted mt-4 !text-[0.7rem]">
          <Plus className="h-3 w-3" /> {creating ? "Creating…" : "Create code"}
        </button>
      </div>

      <div data-testid={ADMIN_TRAIN.codesList} className="glass-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Label</th>
              <th className="px-4 py-3">Roles</th>
              <th className="px-4 py-3">Uses</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.id} data-testid={ADMIN_TRAIN.codeRow(c.id)} className="border-b border-white/5">
                <td className="px-4 py-3">
                  <code className="text-lifted-green">{c.code}</code>
                  {c.is_system_default && <span className="ml-2 chip chip--purple !text-[0.6rem]">SYSTEM</span>}
                </td>
                <td className="px-4 py-3 text-lifted-text">{c.label}</td>
                <td className="px-4 py-3 text-xs text-lifted-dim">
                  {c.allowed_roles?.length ? c.allowed_roles.join(", ") : "any"}
                </td>
                <td className="px-4 py-3 text-xs text-lifted-dim">{c.used_count} / {c.max_uses ?? "∞"}</td>
                <td className="px-4 py-3">
                  {c.active
                    ? <span className="chip chip--green">Active</span>
                    : <span className="chip">Inactive</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button data-testid={ADMIN_TRAIN.codeToggle(c.id)} onClick={() => toggle(c.id)} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem]"><Power className="h-3 w-3" /> {c.active ? "Deactivate" : "Activate"}</button>
                    <button data-testid={ADMIN_TRAIN.codeDelete(c.id)} onClick={() => del(c.id, c.is_system_default)} disabled={c.is_system_default} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem] disabled:opacity-40 hover:!text-rose-300"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {codes.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-lifted-mute">No codes.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
